/**
 * MusicAlbumDetailPage - アルバム詳細（アートワーク + 収録曲一覧）
 *
 * 再生 / シャッフル を押すとキューを差し替える（実際の音声再生には未接続）。
 */

import { ArrowDownToLine, Check, CircleArrowDown, Disc3, Loader2, MicVocal, Play, Shuffle } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'

import { getMusicAlbumDetail } from '@/api/api'
import type { MusicAlbumDetailResponse, MusicAlbumTrackItem } from '@/api/type'
import { Header } from '@/components/shared/header'
import { AuthImage } from '@/components/ui/auth-image'
import { Spinner } from '@/components/ui/spinner'
import { useProductDownload } from '@/hooks/useEpisodeDownload'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'
import { type MusicTrack, useMusicStore } from '@/store/useMusicStore'

import {
  ACCENT_COLOR,
  AUTOPLAY_TRACK_PARAM,
  DOWNLOADED_ONLY_PARAM,
  DOWNLOADED_ONLY_QUERY,
  MUSIC_DOMAIN,
  MUSIC_MIME_TYPE,
} from './constants'
import { formatReleaseDate, formatReleaseYear, formatTotalDuration } from './format'
import { buildOfflineAlbumDetail, toOfflineMusicMeta } from './offline-music'
import { MusicTrackMenu } from './track-menu'
import { useOfflineEpisodes } from './use-offline-ids'

/** アルバム詳細のレスポンスをプレイヤーのトラック形式に変換する */
const toMusicTracks = (album: MusicAlbumDetailResponse, tracks: MusicAlbumTrackItem[]): MusicTrack[] =>
  tracks.map((track) => ({
    episodeId: track.episode_id,
    productId: album.product_id,
    title: track.title,
    artist: album.artist_name,
    artistTagId: album.artist_tag_id,
    album: album.title,
    thumbnail: album.thumbnail,
    duration: track.duration,
  }))

export const MusicAlbumDetailPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const playQueue = useMusicStore((s) => s.playQueue)
  const setPlayerOpen = useMusicStore((s) => s.setPlayerOpen)
  const { product_id: productIdParam } = useParams<{ product_id: string }>()
  const productId = Number(productIdParam)
  const offlineEpisodes = useOfflineEpisodes(productId)
  const { downloadProduct } = useProductDownload()

  // ダウンロード済みライブラリからの遷移では、オフライン保存済みの曲だけを表示する
  const isDownloadedOnly = searchParams.get(DOWNLOADED_ONLY_PARAM) === '1'

  const [remoteAlbum, setRemoteAlbum] = useState<MusicAlbumDetailResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)

  useEffect(() => {
    if (!Number.isFinite(productId)) {
      setError('アルバムが指定されていません')
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)
    getMusicAlbumDetail(productId)
      .then((res) => {
        if (cancelled) return
        setRemoteAlbum(res)
      })
      .catch(() => {
        if (cancelled) return
        setError('アルバムの取得に失敗しました')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [productId])

  /** サーバーに繋がらないときは保存済みデータからアルバム詳細を組み立てる */
  const offlineAlbum = useMemo(
    () => (offlineEpisodes === null ? null : buildOfflineAlbumDetail(offlineEpisodes, productId)),
    [offlineEpisodes, productId],
  )
  const album = remoteAlbum ?? offlineAlbum

  const subtitle = [album?.genre, formatReleaseYear(album?.released_at ?? null)].filter(Boolean).join(' · ')

  const offlineIds = useMemo(() => new Set((offlineEpisodes ?? []).map((e) => e.episodeId)), [offlineEpisodes])

  const isAllDownloaded = album !== null && album.tracks.every((t) => offlineIds.has(t.episode_id))

  /** 表示する曲（ダウンロード済み表示ではオフライン保存済みのものだけに絞る） */
  const visibleTracks = useMemo(() => {
    if (!album) return []
    if (!isDownloadedOnly) return album.tracks
    return album.tracks.filter((t) => offlineIds.has(t.episode_id))
  }, [album, isDownloadedOnly, offlineIds])

  /** 再生キューに入れるトラック（表示中の曲と同じ並び） */
  const queueTracks = useMemo(() => (album === null ? [] : toMusicTracks(album, visibleTracks)), [album, visibleTracks])

  // エピソード直リンク（/p/:episode_id）から送られてきたときは、その曲から再生を始める
  const autoPlayEpisodeId = Number(searchParams.get(AUTOPLAY_TRACK_PARAM))
  const autoPlayedRef = useRef(false)

  useEffect(() => {
    if (autoPlayedRef.current) return
    if (!Number.isFinite(autoPlayEpisodeId) || autoPlayEpisodeId <= 0) return

    const index = queueTracks.findIndex((t) => t.episodeId === autoPlayEpisodeId)
    if (index < 0) return

    autoPlayedRef.current = true
    playQueue(queueTracks, index)
    setPlayerOpen(true)
  }, [autoPlayEpisodeId, queueTracks, playQueue, setPlayerOpen])

  // 曲を直接選んだときは1曲だけ再生する（続けて聴きたい場合は上の再生/シャッフルを使う）
  const handlePlayTrack = (index: number) => {
    const track = queueTracks.at(index)
    if (!track) return
    playQueue([track], 0)
  }

  const totalDuration = isDownloadedOnly
    ? visibleTracks.reduce((sum, t) => sum + t.duration, 0)
    : (album?.total_duration ?? 0)

  // 保存状況が揃うまで曲を出さない（未保存の曲が一瞬見えるのを防ぎ、オフライン時のフォールバックも待つ）
  const isPending = isLoading || offlineEpisodes === null

  /** アルバム内の全曲を順番にダウンロードする（保存済みはスキップされる） */
  const handleDownloadAll = () => {
    if (!album || isDownloading) return

    setIsDownloading(true)
    void downloadProduct(
      toMusicTracks(album, album.tracks).map((track) => ({
        episodeId: track.episodeId,
        productId: track.productId,
        domain: MUSIC_DOMAIN,
        title: track.title,
        mimeType: MUSIC_MIME_TYPE,
        music: toOfflineMusicMeta(track),
      })),
    ).finally(() => setIsDownloading(false))
  }

  const downloadIcon = () => {
    if (isDownloading) return <Loader2 size={20} className="animate-spin" />
    if (isAllDownloaded) return <Check size={20} />
    return <ArrowDownToLine size={20} />
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header title="" onClose={() => navigate(-1)} />

      {isPending && (
        <div className="flex flex-1 justify-center py-12">
          <Spinner size={40} />
        </div>
      )}
      {!isPending && error && album === null && (
        <div className="flex-1 py-12 text-center text-muted-foreground">{error}</div>
      )}

      {!isPending && album && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col items-center px-8 pt-2">
            <div className="aspect-square w-full max-w-[280px] overflow-hidden rounded-lg bg-white/10">
              {album.thumbnail ? (
                <AuthImage
                  src={album.thumbnail}
                  alt={album.title}
                  lazy={false}
                  className="size-full object-cover"
                  cacheKey={`music-album-${album.product_id}`}
                />
              ) : (
                <div className="flex size-full items-center justify-center">
                  <Disc3 size={48} className="text-muted-foreground" />
                </div>
              )}
            </div>

            <div className="mt-4 text-center text-xl font-bold">{album.title}</div>
            {album.artist_name && (
              <button
                type="button"
                disabled={album.artist_tag_id === null}
                onClick={() => {
                  const path = PATHS.MUSIC.ARTIST_DETAIL.replace(':tag_id', String(album.artist_tag_id))
                  navigate(isDownloadedOnly ? `${path}?${DOWNLOADED_ONLY_QUERY}` : path)
                }}
                className="mt-1 max-w-full truncate border-none text-lg"
              >
                {album.artist_name}
              </button>
            )}
            {subtitle && <div className="mt-1 text-sm text-muted-foreground">{subtitle}</div>}
          </div>

          <div className="mt-5 flex items-center justify-center gap-4 px-4">
            <button
              type="button"
              aria-label="シャッフル再生"
              onClick={() => playQueue(queueTracks, 0, { shuffle: true })}
              className="flex size-12 items-center justify-center rounded-full border-none bg-white/10"
              style={{ color: ACCENT_COLOR }}
            >
              <Shuffle size={20} />
            </button>
            <button
              type="button"
              onClick={() => playQueue(queueTracks, 0)}
              className="flex min-w-[180px] items-center justify-center gap-2 rounded-full border-none bg-white py-3 font-semibold text-black"
            >
              <Play size={18} fill="black" />
              再生
            </button>
            <button
              type="button"
              aria-label={isAllDownloaded ? 'ダウンロード済み' : 'アルバムをダウンロード'}
              disabled={isAllDownloaded || isDownloading}
              onClick={handleDownloadAll}
              className="flex size-12 items-center justify-center rounded-full border-none bg-white/10 disabled:opacity-50"
              style={{ color: ACCENT_COLOR }}
            >
              {downloadIcon()}
            </button>
          </div>

          <ul className="mt-5 pl-4">
            {visibleTracks.map((track, i) => (
              <li key={track.episode_id} className="flex items-center gap-3">
                <span className="w-6 shrink-0 text-right text-muted-foreground">{track.index}</span>
                <span className="flex min-w-0 flex-1 items-center gap-2 border-b border-white/10 pr-4">
                  <button
                    type="button"
                    onClick={() => handlePlayTrack(i)}
                    className="min-w-0 flex-1 truncate border-none py-4 text-left"
                  >
                    {track.title}
                  </button>
                  {track.lyrics_timing && (
                    <MicVocal
                      size={16}
                      aria-label={track.lyrics_timing === 'word' ? '歌詞（音節同期）' : '歌詞あり'}
                      className={cn(
                        'shrink-0',
                        track.lyrics_timing === 'word' ? 'text-foreground/70' : 'text-muted-foreground',
                      )}
                    />
                  )}
                  {offlineIds.has(track.episode_id) && (
                    <CircleArrowDown size={18} className="shrink-0 text-muted-foreground" />
                  )}
                  <MusicTrackMenu
                    downloadedOnly={isDownloadedOnly}
                    track={{
                      episodeId: track.episode_id,
                      productId: album.product_id,
                      title: track.title,
                      artist: album.artist_name,
                      artistTagId: album.artist_tag_id,
                      album: album.title,
                      thumbnail: album.thumbnail,
                      duration: track.duration,
                    }}
                  />
                </span>
              </li>
            ))}
          </ul>

          <div className="px-4 py-4 text-sm text-muted-foreground">
            {album.released_at && <div>{formatReleaseDate(album.released_at)}</div>}
            <div>
              {visibleTracks.length}曲、{formatTotalDuration(totalDuration)}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
